import { BodyText, Flex, IconContainer } from '@utilitywarehouse/hearth-react';
import clsx from 'clsx';

import {
  IconState,
  Product,
  getTierServiceState,
} from '@components/WelcomeBonusSection/TierService/tierServiceState';
import { Tier } from '@components/WelcomeBonusSection/tiers';
import { ProductType } from '@generated/gql/graphql';

import './styles.scss';

type TierServiceProps = {
  tier: Tier;
  tierServices: ProductType[];
};

type TierServiceSelectedProps = {
  selectedProducts: Product[];
};

type TierServiceRemainingProps = {
  remainingProducts: Product[];
};

const TIER_SERVICE_SIZE = '32px';

const TierServiceIdle = () => (
  <Flex
    className="welcomeBonus__dashed"
    borderWidth="1"
    borderColor="strong"
    borderRadius="md"
    overflow="clip"
    flexShrink="0"
    width={TIER_SERVICE_SIZE}
    height={TIER_SERVICE_SIZE}
  />
);

const TierServiceSelected = ({
  selectedProducts,
}: TierServiceSelectedProps) => {
  if (selectedProducts.length === 0) {
    return null;
  }

  return (
    <Flex marginLeft="100">
      {selectedProducts.map((selectedProduct, index) => {
        const isLast = index === selectedProducts.length - 1;

        return (
          <Flex
            borderWidth="1"
            borderColor="strong"
            borderRadius="md"
            overflow="clip"
            flexShrink="0"
            className={isLast ? undefined : 'tierService__icon--slotted'}
          >
            <IconContainer
              variant="subtle"
              colorScheme={selectedProduct.colorScheme}
              size="sm"
            >
              {selectedProduct.Icon}
            </IconContainer>
          </Flex>
        );
      })}
    </Flex>
  );
};

const TierServiceRemaining = ({
  remainingProducts,
}: TierServiceRemainingProps) => {
  if (remainingProducts.length === 0) {
    return null;
  }

  return (
    <>
      <BodyText size="sm" color="secondary">
        Add a service
      </BodyText>

      <Flex
        className={clsx(
          'welcomeBonus__dashed',
          'tierService',
          'tierService__viewport'
        )}
        borderWidth="1"
        borderColor="strong"
        borderRadius="md"
        overflow="clip"
        flexShrink="0"
        marginLeft="100"
        justifyContent="center"
        width={TIER_SERVICE_SIZE}
        height={TIER_SERVICE_SIZE}
        aria-hidden
      >
        {remainingProducts.length > 1 ? (
          <Flex
            className={clsx(
              'tierService__viewport__track',
              `tierService__viewport__track--count-${remainingProducts.length}`
            )}
            direction="column"
            width={TIER_SERVICE_SIZE}
            height={TIER_SERVICE_SIZE}
          >
            {/* Appending the first icon allows for a seamless transition loop. */}
            {[...remainingProducts, remainingProducts[0]].map(
              (remainingProduct, index) => (
                <IconContainer
                  key={
                    index === remainingProducts.length
                      ? `${remainingProduct.type}-loop`
                      : remainingProduct.type
                  }
                  variant="subtle"
                  colorScheme={remainingProduct.colorScheme}
                  size="sm"
                  borderRadius="none"
                >
                  {remainingProduct.Icon}
                </IconContainer>
              )
            )}
          </Flex>
        ) : (
          <IconContainer
            variant="subtle"
            colorScheme={remainingProducts[0].colorScheme}
            size="sm"
          >
            {remainingProducts[0].Icon}
          </IconContainer>
        )}
      </Flex>
    </>
  );
};

export const TierService = ({ tier, tierServices }: TierServiceProps) => {
  const state = getTierServiceState(tier, tierServices);

  switch (state.type) {
    case IconState.IDLE: {
      return <TierServiceIdle />;
    }

    case IconState.SELECTED: {
      return <TierServiceSelected selectedProducts={state.selectedProducts} />;
    }

    case IconState.REMAINING: {
      return (
        <TierServiceRemaining remainingProducts={state.remainingProducts} />
      );
    }
  }
};
